import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import * as api from '../../db/api.js'

/**
 * Página de redirect do Google OAuth.
 *
 * Lê ?code=&state= da URL, manda autenticado pro backend que troca
 * code→tokens, persiste a oauth_connection e retorna a lista de contas
 * Google Ads que o usuário pode vincular. Em seguida redireciona para
 * o picker, passando connection_id + lista via location.state (não
 * trafegamos pela URL para evitar logging em CDN/proxy).
 *
 * Desde a issue #72 esta página atende DUAS integrações Google: Ads e
 * Calendar. As duas compartilham o mesmo redirect_uri registrado no Cloud
 * Console, e o `provider` vem assinado dentro do state — por isso o dispatch
 * abaixo. O state é base64 de um JSON (a assinatura protege contra
 * adulteração, não é segredo), então dá para lê-lo aqui sem consultar o
 * backend; se a leitura falhar, assumimos Google Ads, que é o fluxo antigo.
 */
function providerDoState(rawState) {
  try {
    const b64 = rawState.replace(/-/g, '+').replace(/_/g, '/')
    const json = JSON.parse(atob(b64 + '='.repeat((4 - (b64.length % 4)) % 4)))
    return json.p || 'google_ads'
  } catch {
    return 'google_ads'
  }
}

export default function GoogleAdsCallback() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const [error, setError] = useState('')
  const [provider, setProvider] = useState('google_ads')
  const ran = useRef(false)

  useEffect(() => {
    // Guard contra StrictMode double-mount: o code do Google é one-shot,
    // chamar exchange duas vezes resulta em invalid_grant.
    if (ran.current) return
    ran.current = true

    const code = params.get('code')
    const state = params.get('state')
    const errParam = params.get('error')

    if (errParam) {
      setError(`Google rejeitou o consent: ${errParam}`)
      return
    }
    if (!code || !state) {
      setError('Resposta do Google incompleta (faltam code/state).')
      return
    }

    const prov = providerDoState(state)
    setProvider(prov)

    if (prov === 'google_calendar') {
      // O client da agenda é gravado no sessionStorage antes do redirect: o
      // state assinado não carrega client_id, e o AppContext pode ainda não
      // ter restaurado o cliente selecionado quando esta página monta.
      const clientId = sessionStorage.getItem('agenda_calendar_client_id')
      if (!clientId) {
        setError('Não foi possível identificar o cliente da agenda. Refaça a conexão pela Agenda.')
        return
      }
      api
        .agCalendarCallback({ code, state, clientId })
        .then(() => {
          sessionStorage.removeItem('agenda_calendar_client_id')
          navigate('/agenda', { replace: true, state: { calendarConnected: true } })
        })
        .catch((err) => {
          setError(err.message || 'Falha ao conectar o Google Calendar.')
        })
      return
    }

    api
      .completeGoogleAdsOAuth({ code, state })
      .then((data) => {
        navigate('/integrations/google-ads/contas', {
          replace: true,
          state: {
            connectionId: data.connection_id,
            googleEmail: data.google_email,
            customers: data.available_customers || [],
          },
        })
      })
      .catch((err) => {
        setError(err.message || 'Falha ao finalizar conexão com o Google.')
      })
  }, [params, navigate])

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        fontFamily: 'Inter, sans-serif',
        padding: 24,
      }}
    >
      <div style={{ maxWidth: 480, textAlign: 'center' }}>
        {error ? (
          <>
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>
              Não foi possível conectar
            </div>
            <div style={{ color: 'var(--ter, #6e6e73)', marginBottom: 24 }}>
              {error}
            </div>
            <button
              className="btn btn-black btn-sm"
              onClick={() =>
                navigate(provider === 'google_calendar' ? '/agenda' : '/perfil', {
                  replace: true,
                })
              }
            >
              {provider === 'google_calendar' ? 'Voltar para a Agenda' : 'Voltar para Perfil'}
            </button>
          </>
        ) : provider === 'google_calendar' ? (
          <>
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>
              Conectando o Google Calendar…
            </div>
            <div style={{ color: 'var(--ter, #6e6e73)' }}>
              Estamos validando o consentimento e vinculando o calendário à sua agenda.
            </div>
          </>
        ) : (
          <>
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>
              Finalizando conexão com Google Ads…
            </div>
            <div style={{ color: 'var(--ter, #6e6e73)' }}>
              Estamos validando o consentimento e listando suas contas.
            </div>
          </>
        )}
      </div>
    </div>
  )
}
